<template>
  <v-card dark class="modern-card overflow-hidden" elevation="10">
    <div class="glow-bg"></div>

    <v-card-text class="pa-6 relative-z">
      <div class="d-flex align-center mb-6 slide-in-item delay-1">
        <v-sheet color="primary" class="d-flex align-center justify-center rounded-lg mr-4 gradient-icon-box" width="48" height="48" elevation="4">
          <v-icon color="white" large>mdi-file-excel-box</v-icon>
        </v-sheet>
        <div>
          <h2 class="text-h5 font-weight-bold white--text mb-1">Instruktor Export</h2>
          <div class="text-subtitle-2 grey--text text--lighten-1">Stundenlisten & Kursdaten herunterladen</div>
        </div>
      </div>

      <v-divider class="mb-6 opacity-20 slide-in-item delay-2"></v-divider>

      <v-row dense class="mb-2">
        <v-col cols="12" md="6" class="slide-in-item delay-3">
          <user-search
            :value="selectedInstructorId"
            ignoreRights
            instructor
            label="Instruktor wählen"
            @update="updateSelectedUser"
            outlined
            color="primary"
            background-color="rgba(255,255,255,0.03)"
            :error-messages="!selectedInstructorId && highlightErrors ? 'Bitte wählen' : ''"
            prepend-inner-icon="mdi-account-search"
            class="rounded-lg modern-input"
            height="56"></user-search>
        </v-col>

        <v-col cols="12" md="6" class="slide-in-item delay-4">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
            content-class="rounded-lg">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formattedDateRange"
                label="Zeitraum wählen"
                prepend-inner-icon="mdi-calendar-range"
                readonly
                v-bind="attrs"
                v-on="on"
                outlined
                color="primary"
                background-color="rgba(255,255,255,0.03)"
                :error-messages="dates.length === 0 && highlightErrors ? 'Zeitraum nötig' : ''"
                class="rounded-lg modern-input"
                height="56"></v-text-field>
            </template>
            <v-date-picker v-model="dates" range no-title scrollable locale="de-CH" color="primary" class="rounded-lg">
              <v-spacer></v-spacer>
              <v-btn text small class="font-weight-bold" color="grey" @click="menu = false">Abbrechen</v-btn>
              <v-btn text small class="font-weight-bold" color="primary" @click="$refs.menu.save(dates)">OK</v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <v-row dense class="slide-in-item delay-5 mt-2">
        <v-col cols="12">
          <v-btn block x-large :loading="loading" @click="triggerDownload" class="action-btn rounded-lg white--text text-subtitle-1" elevation="6">
            <v-icon left>mdi-download</v-icon>
            Export Starten
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import api from "../../../api"
import userSearch from "@/components/userSearch.vue"

export default {
  name: "InstructorCourseListWidget",
  components: { userSearch },
  data() {
    return {
      selectedInstructorId: null,
      dates: [],
      menu: false,
      loading: false,
      highlightErrors: false,
    }
  },
  computed: {
    formattedDateRange() {
      if (!this.dates || this.dates.length === 0) return ""
      const [start, end] = [...this.dates].sort()
      if (!end) return this.formatDate(start)
      return `${this.formatDate(start)} - ${this.formatDate(end)}`
    },
    isValid() {
      return this.selectedInstructorId && this.dates.length > 0
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return null
      const [year, month, day] = date.split("-")
      return `${day}.${month}.${year}`
    },
    updateSelectedUser(u) {
      this.selectedInstructorId = u
    },
    triggerDownload() {
      this.highlightErrors = true

      // Kleines Vibrations-Feedback bei Fehler (falls User-Browser das unterstützt)
      if (!this.isValid && navigator.vibrate) {
        navigator.vibrate(50)
      }

      if (this.isValid) {
        this.downloadList()
      }
    },
    async downloadList() {
      if (!this.isValid) return

      this.loading = true

      const sortedDates = [...this.dates].sort()
      const startDate = sortedDates[0]
      const endDate = sortedDates[1] || sortedDates[0]

      try {
        await api.downloadInstructorCourseList({
          instructorId: this.selectedInstructorId,
          startDate: startDate,
          endDate: endDate,
        })
        this.$store.dispatch("showSnackbar", { text: "Export erfolgreich gestartet 🚀", color: "success" })
      } catch (error) {
        console.error("Download failed", error)
        this.$store.dispatch("showSnackbar", { text: "Fehler beim Export", color: "error" })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// --- Variables ---
$card-bg: #1e1e1e; // Sehr dunkles Grau statt reinem Schwarz
$primary-gradient: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-secondary-base) 100%);
// Fallback falls CSS Vars nicht greifen:
$primary-gradient-fallback: linear-gradient(135deg, #1976d2 0%, #9c27b0 100%);

// --- Card Styling ---
.modern-card {
  background-color: $card-bg !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  // Subtiler Glow oben rechts
  .glow-bg {
    position: absolute;
    top: -50px;
    right: -50px;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, var(--v-primary-base) 0%, transparent 70%);
    opacity: 0.15;
    filter: blur(40px);
    z-index: 0;
    pointer-events: none;
  }
}

.relative-z {
  position: relative;
  z-index: 1;
}

// --- Gradient Icon Box ---
.gradient-icon-box {
  background: $primary-gradient-fallback !important; // Vuetify Farben nutzen falls möglich
  border: 1px solid rgba(255, 255, 255, 0.1);
}

// --- Inputs ---
.modern-input {
  // Input Transition
  ::v-deep .v-input__slot {
    transition: background-color 0.2s ease, border-color 0.2s ease;
  }

  // Fokus Effekt
  &.v-input--is-focused ::v-deep .v-input__slot {
    background-color: rgba(255, 255, 255, 0.08) !important;
    box-shadow: 0 0 0 2px rgba(var(--v-primary-base), 0.2); // Leuchtrahmen
  }
}

// --- Action Button ---
.action-btn {
  background: $primary-gradient-fallback !important;
  border: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.5px;

  // Wenn Disabled, Gradient entfernen und grau machen
  &.v-btn--disabled {
    background: rgba(255, 255, 255, 0.12) !important;
    opacity: 0.4;
    color: rgba(255, 255, 255, 0.3) !important;
  }

  &:not(.v-btn--disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px -5px rgba(var(--v-primary-base), 0.5) !important;
    filter: brightness(1.1);
  }

  &:not(.v-btn--disabled):active {
    transform: translateY(0);
  }
}

.opacity-20 {
  opacity: 0.2;
}

// --- Animations ---
// Elemente fliegen von unten rein
@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-in-item {
  opacity: 0; // Startzustand
  animation: slideUpFade 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

// Staggered Delays
.delay-1 {
  animation-delay: 0.1s;
}
.delay-2 {
  animation-delay: 0.2s;
}
.delay-3 {
  animation-delay: 0.3s;
}
.delay-4 {
  animation-delay: 0.4s;
}
.delay-5 {
  animation-delay: 0.5s;
}
</style>
